import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { DataTablesModule } from 'angular-datatables';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { loadPaidDonations } from 'app/manage-auctions/features/auction.actions';
import { selectPaidDonations } from 'app/manage-auctions/features/auction.selectors';
import { ModalAuctionDetailService } from 'app/manage-auctions/services/modal-auction-detail/modal-auction-detail.service';
import { Subject } from 'rxjs';
@Component({
  selector: 'app-auction-paid-tab',
  standalone: true,
  imports: [DataTablesModule],
  templateUrl: './auction-paid-tab.component.html',
  styleUrl: './auction-paid-tab.component.scss'
})
export class AuctionPaidTabComponent implements OnInit{
  @ViewChild('auctionPaidDatatable') datatableElement!: ElementRef;
  @Input() auction;

  public paidDonations: any[];
  public filteredPaidDonations: any[];
  public dtOptions = {};
  public dtTrigger: Subject<ADTSettings> = new Subject<ADTSettings>();
  private selectedOption = 'All'

  constructor(
    private store:Store,
    private modalAuctionDetailService: ModalAuctionDetailService
  ) { }

  private paidDonationsSelector$ = this.store.pipe(
    takeUntilDestroyed(),
    select(selectPaidDonations)
  )

  ngOnInit(){
    this.store.dispatch(
      loadPaidDonations({campaignId: this.auction.id})
    )

    this.paidDonationsSelector$.subscribe(list => {
      if(!list){
        return;
      }
      
      this.paidDonations = list;
      this.filteredPaidDonations = [...list];
      this.renderDatatable();
    })
  }

  groupOptionsByName(){
    return this.paidDonations.reduce((acc, record) => {
      const name = record.presetName;
      const id = record.presetId;
      const key = `${name}|${id}`;
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map<{name: string, id: string}, number>());
  }

  renderDatatable(){
    this.filteredPaidDonations = this.paidDonations.filter(donation => {
        if(this.selectedOption === 'All'){
            return true;
        }

        return donation.presetId === this.selectedOption;
    })

    const allDropdownColumns = [ 
        'Name',
        'Email',
        'First Name',
        'Last Name',
        'Phone',
        'Address',
        'Option',
        'Status',
        'Type',
        'Amount',
        'Tax',
        'Date',
        'Method',
        'Info',
        'Info Selected',
    ]

    const defaultVisibleColumns = [0,1,4,5,6,7,8,9,10,11,12];
    const locationPass = "report.campaigns.paid"
    window['datatableColumnsStateService'].initColumnsIfEmpty(locationPass,
    allDropdownColumns, defaultVisibleColumns);

    const currentColumns = window['datatableColumnsStateService'].getColumns(locationPass)

    if(!this.auction?.additionalInfoDesc || this.auction?.additionalInfoDesc === ''){
        const infoColumnIndex = allDropdownColumns.findIndex(x => x === 'Info')
        currentColumns[infoColumnIndex] = null;
    }

    if(!this.auction?.additionalInfoSelectDesc || this.auction?.additionalInfoSelectDesc === ''){
        const infoSelectIndex = allDropdownColumns.findIndex(x => x === 'Info Selected')
        currentColumns[infoSelectIndex] = null;
    }

    const visibleColumnIndexes = currentColumns
        .map((col, index) => (col && col.selected ? index : null)) // preserve original index
        .filter(index => index !== null); 
    
    const defaultOrder = currentColumns.findIndex(x => x.columnName === "Date")

    const self = this;
    this.dtOptions = {
        data: this.filteredPaidDonations,
        autoWidth: true,
        pageLength: 10,
        order: [[ defaultOrder === - 1 ? 0 : defaultOrder, "desc" ]],
        columnDefs: [
            { targets: visibleColumnIndexes, visible: true },
            { targets: '_all', visible: false },
        ],
        fixedColumns: {
            left: 1,
        },
        paging: true,
        deferRender: true,
        destroy: true,
        dom: "<'table__header--paybee d-flex align-items-center justify-content-between pb-3 pt-3'<''B><'d-flex align-items-center'<'#auctionDonationsList-columns-filter.mx-3'><'filter--reminder mr-3'><''f>>><'table-responsive't><'table__footer--paybee d-flex align-items-center justify-content-between p-4'ip>",
        //dom: "<'table__header--paybee d-flex align-items-center justify-content-between pb-3 pt-3'<''B><'d-flex align-items-center'<'auctionPaidFilter mr-3'><''f>>><'table-responsive't><'table__footer--paybee d-flex align-items-center justify-content-between p-4'ip>", 
        buttons: [{
            className: 'btn-copy-code btn btn-outline-grey btn-sm px-2 waves-effect',
            extend: 'excel',
            filename: 'Auction_PaidItems',
            text: "<i class='fas fa-cloud-download-alt mr-1'></i> Export",
            title: null,
            exportOptions: {
                stripHtml: true,
                columns: "thead th:not(.no-export)"
            },
            action: function (e, dt, button, config){
                window['orgMain'].downloadPaidAuctionDonations(self.paidDonations);
            }
        }],
        columns: [
        {
            data: "payer.name",
            render(name, type, row) {
                if(type === 'display'){
                    return `<span 
                                data-toggle="fullNameTooltip"
                                data-placement="top"
                                data-original-title="${name}"
                                class="table-name-container blue-text cursor--pointer">${name}</span>`
                }
                return name;
            },
            createdCell: function(cell, cellData, rowData, row, col){
              const callback = () => {  
                self.openAuctionDetailModal(rowData.payer.id);
              }
              cell?.removeEventListener('click', callback);
              cell?.addEventListener('click', callback);
            }
        },
        {
            data: "payer.email",
        },
        {
            data: "payer.firstName",
        },
        {
            data: "payer.lastName",
        },
        {
            data: "payer.phone",
            defaultContent: "NA",
            render : function (phone) {
                if(phone != undefined && phone != null && phone != "") {
                    return phone
                } else {return "NA"}
            }
        },
        {
            data: "payer.address",
            defaultContent: "NA",
            render : function (address) {
                if(address != undefined && address != null && address.street1 != "") {
                    return window['utils'].getStreetAddressText(address, true)
                } else {return "NA"}
            }
        },
        {
            data: "presetName",
        },
        {
            data: "status",
            render: function(data, type, row, meta ) {
                const delivered = row.delivered || 0;
                if(delivered == 1) {
                    return "Yes"
                } else if(delivered == 2) {
                    return "No"
                } else {
                    return "NA"
                }
            }
        },
        {
            data: "amount",
            type: "num-fmt",
            render: function(data, type, row ) {
                return window['utils'].getAmountDisplay(row.amount)
            }
        },
        {
            data: "optionValueProvided",
            type: "num-fmt",
            render: function(data, type, row ) {
                return window['utils'].getAmountDisplay(data)
            }
        },
        {
            data: "taxCollected",
            type: "num-fmt",
            render: function(taxCollected, type, row ) {
                return window['utils'].getAmountDisplay(row.taxCollected)
            }
        },
        {
            data: "date",
            type: "date",
            render: function(data, type, row ) {
                return window['orgMain'].dateString(row.date)
            }
        },
        {
            data: "method",
        },
        {
            "data": "additionalInfo",
            "render": function(addInfo, type, row ) {
                if(addInfo != "") {
                    return addInfo.replace(/<[^>]*>/g, '')
                } else {
                    return "NA"
                }
            },
        },
        {
            "data": "additionalInfoSelected",
            "render": function(addInfoSelect, type, row ) {
                if(addInfoSelect != "") {
                    return addInfoSelect
                } else {
                    return "NA"
                }
            },
        },
        ],
        initComplete: () => {
            let columnsDropdownHTML =  window['orgMain'].getColumnsDropdownHTML(currentColumns, self.dtOptions)
            $("#auctionDonationsList-columns-filter").html(columnsDropdownHTML);
            window['orgMain'].loadColumnsDropdown('auctionDonationsList-columns-filter', 'auctionPaidDatatable', locationPass);
          },
        drawCallback: function(event){
            const groupedOptions = self.groupOptionsByName();
            let filterLabel = 'All'
            const filterPresetOptionList = [];
             groupedOptions.forEach((count,key) => {
                const [name, id] = key.split('|');
    
                let classes = '';
                if(self.selectedOption === id) {
                    classes = 'active';
                    filterLabel = `${name} (${count})`;
                }
                filterPresetOptionList.push(`<li id="auction-paid-${id}" data-option-id="${id}" class="dropdown-item body-1 ${classes}"><a>${name} (${count})</a></li>`)
            })
    
            const activeAllClasses = ['dropdown-item', 'body-1']
            if(self.selectedOption === 'All'){
                activeAllClasses.push('active')
            }
    
            $(".auctionPaidFilter").html(
              `<button type="button" data-toggle="dropdown" class="filter-button_max-width campaign__action btn btn--paybee btn--outline-transparent caption dropdown-toggle px-3 py-2" style="border: 1px solid #D8D8D8; background: transparent; border-radius: 4px;">
                <i class="fas fa-filter mr-2"></i>
                <span class="mr-2">
                    ${filterLabel}
                </span>
               </button>
                <ul class="dropdown-menu__max-width dropdown-menu dropdown-menu-right">
                    <li class="${activeAllClasses.join(" ")}" id="auction-paid-all" data-option-id="All"><a>All</a></li>
                    ${filterPresetOptionList.join("")}
                </ul>`);
            $('.dropdown-menu').on('click', function(event) {
                event.stopPropagation();
            });
    
            $('li[id^="auction-paid-"]').on('click', function () {
                const optionId = $(this).data('option-id');
                self.selectedOption = optionId;
                self.renderDatatable();
            });

            window['$']('[data-toggle="cardInfoTooltip"]').tooltip()
            window['$']('[data-toggle="popover-hover-table"]').tooltip()

            const datatableEl = self.datatableElement.nativeElement as HTMLElement;
            const scrollContainer = datatableEl.parentElement;
        
            if (scrollContainer) {
            window['dataTableService'].registerScrollEvent(window['$'](scrollContainer));
            }

            setTimeout(() => {
                window['dataTableService'].scrollCallback(window['$'](scrollContainer))

                window['$']('[data-toggle="fullNameTooltip"]')
                 .filter(function () {
                    const isSelf = this.classList.contains('table-name-container');
                    const container = isSelf ? this : this.querySelector('.table-name-container');
                    return container && container.scrollWidth > container.offsetWidth;
                  })
                  .tooltip();
              })
          }
    }

    setTimeout(() => {
      // race condition fails unit tests if dtOptions isn't sent with dtTrigger
      if (!this.dtTrigger.closed) {
        this.dtTrigger.next(this.dtOptions);
      }
    }, 200);
  }

  openAuctionDetailModal(userId) {
    const data = {
      userId,
      placeOfCall: 'bidderDetails'
    }

    this.modalAuctionDetailService.open(data);
  }
}
